import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Edit3, Trash2, Users, XCircle, AlertCircle, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import {
    fetchLeadStatuses,
    createLeadStatus,
    updateLeadStatus,
    deleteLeadStatus,
    updateActiveClientStatuses
} from "../../services/crmServices/leadStatusService";
import { useAuth } from "../../hooks/useAuth";

export function LeadStatusManagement() {
    const { user } = useAuth();
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [formData, setFormData] = useState({
        label: "",
        value: "",
        color: "#3B82F6",
        isActiveClient: false,
        keyboardShortcut: ""
    });

    useEffect(() => {
        if (user.position.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
            return;
        }
        loadLeadStatuses();
    }, [user.position]);

    const loadLeadStatuses = async () => {
        setLoading(true);
        try {
            const response = await fetchLeadStatuses();
            if (response?.data) {
                setLeadStatuses(response.data);
            }
        } catch (error) {
            console.error("Error loading lead statuses:", error);
            toast.error("Failed to load lead statuses");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.label.trim() || !formData.value.trim()) {
            toast.error("Label and value are required");
            return;
        }

        // Check for duplicate labels
        const isDuplicateLabel = leadStatuses.some(
            (status) =>
                status.label.toLowerCase() === formData.label.toLowerCase() &&
                (!editingStatus || status._id !== editingStatus._id)
        );

        if (isDuplicateLabel) {
            toast.error("Status label already exists");
            return;
        }

        // Check for duplicate values
        const isDuplicateValue = leadStatuses.some(
            (status) =>
                status.value.toLowerCase() === formData.value.toLowerCase() &&
                (!editingStatus || status._id !== editingStatus._id)
        );

        if (isDuplicateValue) {
            toast.error("Status value already exists");
            return;
        }

        // Check for duplicate keyboard shortcuts (if shortcut is provided)
        if (formData.keyboardShortcut && formData.keyboardShortcut.trim()) {
            const isDuplicateShortcut = leadStatuses.some(
                (status) =>
                    status.keyboardShortcut &&
                    status.keyboardShortcut.toUpperCase() === formData.keyboardShortcut.toUpperCase() &&
                    (!editingStatus || status._id !== editingStatus._id)
            );

            if (isDuplicateShortcut) {
                toast.error("Keyboard shortcut already assigned to another status");
                return;
            }
        }

        setLoading(true);
        try {
            let response;
            if (editingStatus) {
                response = await updateLeadStatus({ ...formData, _id: editingStatus._id });
            } else {
                response = await createLeadStatus(formData);
            }

            if (response?.status === 200) {
                toast.success(`Lead status ${editingStatus ? "updated" : "created"} successfully`);
                loadLeadStatuses();
                handleCloseModal();
            } else {
                toast.error(`Failed to ${editingStatus ? "update" : "create"} lead status`);
            }
        } catch (error) {
            console.error("Error saving lead status:", error);
            toast.error(`Failed to ${editingStatus ? "update" : "create"} lead status`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (status) => {
        setEditingStatus(status);
        setFormData({
            label: status.label,
            value: status.value,
            color: status.color || "#3B82F6",
            isActiveClient: status.isActiveClient || false,
            keyboardShortcut: status.keyboardShortcut || ""
        });
        setShowCreateModal(true);
    };

    const handleDelete = async (statusId) => {
        if (window.confirm("Are you sure you want to delete this status? This action cannot be undone.")) {
            setLoading(true);
            try {
                const response = await deleteLeadStatus(statusId);
                if (response?.status === 200) {
                    toast.success("Lead status deleted successfully");
                    loadLeadStatuses();
                } else {
                    toast.error("Failed to delete lead status");
                }
            } catch (error) {
                console.error("Error deleting lead status:", error);
                toast.error("Failed to delete lead status");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleToggleActiveClient = async (statusId, newValue) => {
        setLoading(true);
        try {
            const response = await updateLeadStatus({
                _id: statusId,
                isActiveClient: newValue
            });

            if (response?.status === 200) {
                setLeadStatuses((prev) =>
                    prev.map((status) => (status._id === statusId ? { ...status, isActiveClient: newValue } : status))
                );
                toast.success(`Status ${newValue ? "marked as" : "removed from"} Active Client status`);
            } else {
                toast.error("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status");
        } finally {
            setLoading(false);
        }
    };

    const activeClientStatuses = leadStatuses.filter((status) => status.isActiveClient);
    const activeClientCount = activeClientStatuses.length;

    // Add this function to generate value from label
    const generateValueFromLabel = (label) => {
        return label
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s]/g, "") // Remove special characters
            .replace(/\s+/g, "_") // Replace spaces with underscores
            .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
    };

    // Add this function to generate colors
    const generateRandomColor = () => {
        const colors = [
            "#3B82F6", // Blue
            "#10B981", // Green
            "#F59E0B", // Yellow
            "#EF4444", // Red
            "#8B5CF6", // Purple
            "#F97316", // Orange
            "#06B6D4", // Cyan
            "#84CC16", // Lime
            "#EC4899", // Pink
            "#6366F1", // Indigo
            "#14B8A6", // Teal
            "#F472B6", // Rose
            "#A855F7", // Violet
            "#22C55E", // Emerald
            "#F97A9B", // Hot Pink
            "#FBBF24" // Amber
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditingStatus(null);
        setFormData({
            label: "",
            value: "",
            color: generateRandomColor(), // Generate new random color when opening modal
            isActiveClient: false,
            keyboardShortcut: ""
        });
    };

    if (user.position.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <Card className="max-w-md mx-auto mt-8">
                    <CardContent className="text-center py-8">
                        <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">Administrator privileges required to access this page.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Lead Status Management</h1>
                        <p className="text-gray-600">Configure lead statuses and define Active Client criteria</p>
                    </div>
                    <Button onClick={() => setShowCreateModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Status
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Settings className="h-5 w-5 text-blue-600" />
                            <div>
                                <p className="text-sm text-gray-600">Total Statuses</p>
                                <p className="text-2xl font-bold">{leadStatuses.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-green-600" />
                            <div>
                                <p className="text-sm text-gray-600">Active Client Statuses</p>
                                <p className="text-2xl font-bold text-green-600">{activeClientCount}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Active Client Statuses Preview */}
            {activeClientCount > 0 && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <Users className="h-5 w-5 mr-2 text-green-600" />
                            Active Client Statuses
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {activeClientStatuses.map((status) => (
                                <Badge
                                    key={status._id}
                                    style={{ backgroundColor: status.color }}
                                    className="text-white"
                                >
                                    {status.label}
                                </Badge>
                            ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            Leads with these statuses will be categorized as &quot;Active Clients&quot;
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Lead Statuses Table */}
            <Card>
                <CardHeader>
                    <CardTitle>All Lead Statuses</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Status</TableHead>
                                <TableHead>Value</TableHead>
                                <TableHead>Color</TableHead>
                                <TableHead>Shortcut</TableHead>
                                <TableHead>Active Client</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {leadStatuses.map((status) => (
                                <TableRow key={status._id}>
                                    <TableCell>
                                        <Badge style={{ backgroundColor: status.color }} className="text-white">
                                            {status.label}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">{status.value}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center space-x-2">
                                            <div
                                                className="w-4 h-4 rounded border"
                                                style={{ backgroundColor: status.color }}
                                            />
                                            <span className="text-xs">{status.color}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {status.keyboardShortcut ? (
                                            <Badge variant="outline" className="font-mono font-bold">
                                                {status.keyboardShortcut.toUpperCase()}
                                            </Badge>
                                        ) : (
                                            <span className="text-gray-400 text-sm">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={status.isActiveClient || false}
                                            onCheckedChange={(checked) => handleToggleActiveClient(status._id, checked)}
                                            disabled={loading}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex space-x-2">
                                            <Button size="sm" variant="outline" onClick={() => handleEdit(status)}>
                                                <Edit3 className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleDelete(status._id)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {leadStatuses.length === 0 && (
                        <div className="text-center py-8">
                            <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">No lead statuses found</p>
                            <p className="text-sm text-gray-400">Create your first status to get started</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal} onOpenChange={handleCloseModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingStatus ? "Edit Lead Status" : "Create Lead Status"}</DialogTitle>
                        <DialogDescription>
                            {editingStatus ? "Update the lead status details" : "Add a new lead status to the system"}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="label">Status Label *</Label>
                                <Input
                                    id="label"
                                    value={formData.label}
                                    onChange={(e) => {
                                        const newLabel = e.target.value;
                                        setFormData((prev) => ({
                                            ...prev,
                                            label: newLabel,
                                            // Auto-generate value from label (only if not editing or value is empty/auto-generated)
                                            value: !editingStatus ? generateValueFromLabel(newLabel) : prev.value
                                        }));
                                    }}
                                    placeholder="e.g., Interested"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="value">Status Value *</Label>
                                <Input
                                    id="value"
                                    value={formData.value}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            value: e.target.value.toLowerCase().replace(/\s+/g, "-")
                                        }))
                                    }
                                    placeholder="Auto-generated from label"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Auto-generated from label. You can modify it manually.
                                </p>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="color">Color</Label>
                            <div className="flex items-center space-x-2">
                                <Input
                                    id="color"
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                                    className="w-16 h-10"
                                />
                                <Input
                                    value={formData.color}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                                    placeholder="#3B82F6"
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setFormData((prev) => ({ ...prev, color: generateRandomColor() }))}
                                >
                                    Random
                                </Button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Random color generated automatically. Click &quot;Random&quot; for a new color.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="keyboardShortcut">Keyboard Shortcut (Optional)</Label>
                            <Input
                                id="keyboardShortcut"
                                value={formData.keyboardShortcut}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase().slice(0, 1);
                                    // Only allow letters and numbers
                                    if (value === "" || /^[A-Z0-9]$/.test(value)) {
                                        setFormData((prev) => ({ ...prev, keyboardShortcut: value }));
                                    }
                                }}
                                placeholder="E.g., I, N, C"
                                maxLength={1}
                                className="uppercase font-mono text-center"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Single key shortcut for quick status selection during calls (A-Z, 0-9)
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label htmlFor="isActiveClient">Active Client Status</Label>
                                    <p className="text-sm text-gray-600">
                                        Leads with this status are considered &quot;Active Clients&quot;
                                    </p>
                                </div>
                                <Switch
                                    id="isActiveClient"
                                    checked={formData.isActiveClient}
                                    onCheckedChange={(checked) =>
                                        setFormData((prev) => ({ ...prev, isActiveClient: checked }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button type="button" variant="outline" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading}>
                                <Save className="h-4 w-4 mr-2" />
                                {editingStatus ? "Update" : "Create"} Status
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
